import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

function Calendar({ userRole, userClass, userName, inline = false }) {
  const [showModal, setShowModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ date: '', title: '', type: 'holiday', targetClasses: [] });
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  
  useEffect(() => {
    if (userClass) {
      fetchEvents();
      if (userRole === 'admin' || userRole === 'admin1') {
        fetchAvailableClasses();
      }
    }
  }, [userClass, userRole]);

  // Body scroll lock for mobile
  useEffect(() => {
    if (showModal && window.innerWidth <= 768) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);
  
  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/calendar-events/${userClass}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };
  
  const fetchAvailableClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/classes`);
      const classNames = response.data.map(cls => cls.name);
      setAvailableClasses(classNames);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };
  
  const addEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.date || !newEvent.title) {
      alert('Please fill in all fields');
      return;
    }
    if ((userRole === 'admin' || userRole === 'admin1') && selectedClasses.length === 0) {
      alert('Please select at least one class');
      return;
    }
    try {
      if (userRole === 'admin' || userRole === 'admin1') {
        // Add event for selected classes
        const targetClasses = selectedClasses.length > 0 ? selectedClasses : availableClasses;
        const promises = targetClasses.map(cls => 
          axios.post(`${API_BASE_URL}/api/calendar-events`, {
            date: newEvent.date,
            title: newEvent.title,
            type: newEvent.type,
            class: cls,
            createdBy: userName,
            isAdminEvent: true
          })
        );
        await Promise.all(promises);
        alert(`Event added to ${targetClasses.length} class(es) successfully!`);
      } else {
        // Add event for teacher's class
        await axios.post(`${API_BASE_URL}/api/calendar-events`, {
          date: newEvent.date,
          title: newEvent.title,
          type: newEvent.type,
          class: userClass,
          createdBy: userName,
          isAdminEvent: false
        });
        alert('Event added successfully!');
      }
      setNewEvent({ date: '', title: '', type: 'holiday', targetClasses: [] });
      setSelectedClasses([]);
      fetchEvents();
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Error adding event: ' + (error.response?.data?.error || error.message));
    }
  };
  
  const handleClassSelection = (className) => {
    setSelectedClasses(prev => 
      prev.includes(className) 
        ? prev.filter(c => c !== className)
        : [...prev, className]
    );
  };
  
  const selectAllClasses = () => {
    setSelectedClasses(selectedClasses.length === availableClasses.length ? [] : [...availableClasses]);
  };
  
  const deleteEvent = async (eventId) => {
    if (window.confirm('Delete this event? This will remove all instances of this event across all classes.')) {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api/calendar-events/${eventId}`);
        await fetchEvents(); // Refresh events
        alert(response.data.message || 'Event deleted successfully!');
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event: ' + (error.response?.data?.error || error.message));
      }
    }
  };
  
  const editEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      date: event.date,
      title: event.title,
      type: event.type,
      targetClasses: []
    });
    // If it's an admin event, find all classes that have this event
    if (event.isAdminEvent) {
      const eventClasses = events
        .filter(e => e.date === event.date && e.title === event.title && e.type === event.type)
        .map(e => e.class);
      setSelectedClasses([...new Set(eventClasses)]); // Remove duplicates
    } else {
      setSelectedClasses([]);
    }
  };
  
  const updateEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.date || !newEvent.title) {
      alert('Please fill in all fields');
      return;
    }
    if (editingEvent.isAdminEvent && selectedClasses.length === 0) {
      alert('Please select at least one class for admin events');
      return;
    }
    try {
      if (editingEvent.isAdminEvent) {
        // For admin events, delete all old instances and create new ones
        await axios.delete(`${API_BASE_URL}/api/calendar-events/${editingEvent._id}`);
        
        // Create new events for selected classes
        const promises = selectedClasses.map(cls => 
          axios.post(`${API_BASE_URL}/api/calendar-events`, {
            date: newEvent.date,
            title: newEvent.title,
            type: newEvent.type,
            class: cls,
            createdBy: editingEvent.createdBy,
            isAdminEvent: true
          })
        );
        await Promise.all(promises);
        alert(`Event updated for ${selectedClasses.length} class(es) successfully!`);
      } else {
        // For regular events, just update the single event
        await axios.put(`${API_BASE_URL}/api/calendar-events/${editingEvent._id}`, {
          date: newEvent.date,
          title: newEvent.title,
          type: newEvent.type
        });
        alert('Event updated successfully!');
      }
      
      setEditingEvent(null);
      setNewEvent({ date: '', title: '', type: 'holiday', targetClasses: [] });
      setSelectedClasses([]);
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      alert('Error updating event: ' + (error.response?.data?.error || error.message));
    }
  };
  
  const cancelEdit = () => {
    setEditingEvent(null);
    setNewEvent({ date: '', title: '', type: 'holiday', targetClasses: [] });
    setSelectedClasses([]);
  };
  
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  
  const getEventForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.find(event => event.date === dateStr);
  };
  
  const getDayColor = (day, dayOfWeek) => {
    const event = getEventForDate(day);
    if (event) {
      if (event.type === 'holiday') return '#ef4444';
      if (event.type === 'celebration') return '#ec4899';
      return '#fbbf24';
    }
    return dayOfWeek === 0 ? '#ef4444' : '#3b82f6';
  };
  
  const renderDays = () => {
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ padding: '8px' }}></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = (firstDay + day - 1) % 7;
      const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
      const event = getEventForDate(day);
      
      days.push(
        <div key={day} style={{
          padding: '8px',
          textAlign: 'center',
          cursor: 'pointer',
          borderRadius: '4px',
          background: getDayColor(day, dayOfWeek),
          color: 'white',
          fontSize: '14px',
          fontWeight: isToday || event ? 'bold' : 'normal',
          position: 'relative',
          border: isToday ? '4px solid #10b981' : 'none',
          boxShadow: isToday ? '0 0 15px rgba(16, 185, 129, 0.6)' : 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
            {day}
            {isToday && <span style={{ fontSize: '20px', filter: 'hue-rotate(120deg) saturate(2)' }}>📍</span>}
          </div>
          {event && <div style={{ fontSize: '8px', marginTop: '2px', fontWeight: 'bold' }}>{event.title.slice(0, 8)}</div>}
        </div>
      );
    }
    
    return days;
  };
  
  if (inline) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          textAlign: 'center',
          marginBottom: '15px',
          padding: '10px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: 'white',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: '18px' }}>📅 {monthNames[month]} {year}</h3>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
          <button onClick={prevMonth} style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}>‹ Prev</button>
          <button onClick={nextMonth} style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer'
          }}>Next ›</button>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          marginBottom: '8px'
        }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: '600',
              padding: '8px',
              color: '#666'
            }}>{day}</div>
          ))}
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          marginBottom: '20px'
        }}>
          {renderDays()}
        </div>
        
        <div style={{ fontSize: '12px', marginBottom: '15px', color: '#666' }}>
          <span style={{ background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>Red: Sundays/Holidays</span>
          <span style={{ background: '#ec4899', color: 'white', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>Pink: Celebrations</span>
          <span style={{ background: '#fbbf24', color: 'white', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>Yellow: Events/Exams</span>
          <span style={{ background: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Blue: Regular Days</span>
        </div>
        
        {(userRole === 'admin' || userRole === 'admin1' || userName === 'Admin_2') && (
          <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
            <h4 style={{ color: '#1e40af', marginBottom: '10px' }}>
              {editingEvent ? '✏️ Edit Event' : '🔧 Admin: Add Event for Classes'}
            </h4>
            <form onSubmit={editingEvent ? updateEvent : addEvent}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="holiday">Holiday</option>
                  <option value="celebration">Celebration</option>
                  <option value="vacation">Vacation</option>
                  <option value="exam">Exam</option>
                  <option value="pt1">PT1</option>
                  <option value="pt2">PT2</option>
                </select>
              </div>
              
              {(!editingEvent || editingEvent.isAdminEvent) && (
                <div style={{ marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong style={{ fontSize: '12px' }}>Select Classes ({selectedClasses.length}/{availableClasses.length}):</strong>
                  <button 
                    type="button" 
                    onClick={selectAllClasses}
                    style={{ 
                      background: selectedClasses.length === availableClasses.length ? '#dc2626' : '#10b981', 
                      color: 'white', 
                      border: 'none', 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      cursor: 'pointer', 
                      fontSize: '10px' 
                    }}
                  >
                    {selectedClasses.length === availableClasses.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '5px', maxHeight: '100px', overflowY: 'auto' }}>
                  {availableClasses.map(className => (
                    <label key={className} style={{ display: 'flex', alignItems: 'center', fontSize: '11px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedClasses.includes(className)}
                        onChange={() => handleClassSelection(className)}
                        style={{ marginRight: '4px' }}
                      />
                      {className}
                    </label>
                  ))}
                </div>
              </div>
              )}
              
              <input
                type="text"
                placeholder="Event title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '10px' }}
              />
              <button type="submit" style={{
                background: editingEvent ? '#f59e0b' : '#dc2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}>
                {editingEvent ? '✏️ Update Event' : '🔧 Add for Selected Classes'}
              </button>
              {editingEvent && (
                <button type="button" onClick={cancelEdit} style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                  Cancel
                </button>
              )}
            </form>
            
            <div style={{ marginTop: '15px', maxHeight: '150px', overflow: 'auto' }}>
              <h5>Existing Events:</h5>
              {events.map(event => (
                <div key={event._id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '5px',
                  background: editingEvent?._id === event._id ? '#fef3c7' : '#f8f9fa',
                  borderRadius: '4px',
                  marginBottom: '5px',
                  border: editingEvent?._id === event._id ? '1px solid #f59e0b' : 'none'
                }}>
                  <span style={{ fontSize: '12px', flex: 1 }}>
                    {event.date} - {event.title} ({event.type})
                    {event.isAdminEvent && <span style={{ color: '#dc2626', fontWeight: 'bold' }}> • Admin</span>}
                  </span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={() => editEvent(event)} style={{
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px'
                    }}>Edit</button>
                    <button onClick={() => deleteEvent(event._id)} style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '10px'
                    }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div onClick={() => setShowModal(true)} style={{
        cursor: 'pointer',
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        color: 'white',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '5px'
      }}>
        <span style={{ fontSize: '18px' }}>📅 {today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        <span style={{ fontSize: '12px', opacity: '0.8' }}>Click to view full calendar</span>
      </div>
      
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: window.innerWidth <= 768 ? 'white' : 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
          zIndex: 10000,
          overflowY: 'auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: window.innerWidth <= 768 ? '0' : '15px',
            padding: '20px',
            width: window.innerWidth <= 768 ? '100%' : '90%',
            maxWidth: window.innerWidth <= 768 ? 'none' : '500px',
            height: window.innerWidth <= 768 ? '100vh' : 'auto',
            maxHeight: window.innerWidth <= 768 ? 'none' : '80vh',
            overflow: 'auto'
          }}>
            <div style={{ 
              textAlign: 'center',
              marginBottom: '15px',
              padding: '10px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              color: 'white',
              borderRadius: '8px',
              fontSize: window.innerWidth <= 768 ? '16px' : '14px',
              fontWeight: '600'
            }}>
              {today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px',
              position: window.innerWidth <= 768 ? 'sticky' : 'static',
              top: 0,
              background: 'white',
              zIndex: 10,
              paddingBottom: window.innerWidth <= 768 ? '10px' : '0',
              borderBottom: window.innerWidth <= 768 ? '1px solid #e5e7eb' : 'none'
            }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontSize: window.innerWidth <= 768 ? '20px' : '18px' }}>📅 {monthNames[month]} {year}</h3>
              <button onClick={() => setShowModal(false)} style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                padding: window.innerWidth <= 768 ? '10px 14px' : '8px 12px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: window.innerWidth <= 768 ? '16px' : '14px'
              }}>✕</button>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <button onClick={prevMonth} style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}>‹ Prev</button>
              <button onClick={nextMonth} style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}>Next ›</button>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '2px',
              marginBottom: '8px'
            }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '8px',
                  color: '#666'
                }}>{day}</div>
              ))}
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '2px',
              marginBottom: '20px'
            }}>
              {renderDays()}
            </div>
            
            <div style={{ fontSize: '12px', marginBottom: '15px', color: '#666' }}>
              <span style={{ background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>Red: Sundays/Holidays</span>
              <span style={{ background: '#ec4899', color: 'white', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>Pink: Celebrations</span>
              <span style={{ background: '#fbbf24', color: 'white', padding: '2px 6px', borderRadius: '4px', marginRight: '8px' }}>Yellow: Events/Exams</span>
              <span style={{ background: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Blue: Regular Days</span>
            </div>
            
            {(userRole === 'teacher' || userRole === 'admin' || userRole === 'admin1') && (
              <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                <h4 style={{ color: '#1e40af', marginBottom: '10px' }}>
                  {editingEvent ? '✏️ Edit Event' : (userRole === 'admin' || userRole === 'admin1') ? '🔧 Admin: Add Event for Classes' : 'Add Event'}
                </h4>
                <form onSubmit={editingEvent ? updateEvent : addEvent}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                      required
                      style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <select
                      value={newEvent.type}
                      onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
                      style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="holiday">Holiday</option>
                      <option value="celebration">Celebration</option>
                      <option value="vacation">Vacation</option>
                      {(userRole === 'admin' || userRole === 'admin1') && (
                        <>
                          <option value="exam">Exam</option>
                          <option value="pt1">PT1</option>
                          <option value="pt2">PT2</option>
                        </>
                      )}
                    </select>
                  </div>
                  
                  {(userRole === 'admin' || userRole === 'admin1') && (!editingEvent || editingEvent.isAdminEvent) && (
                    <div style={{ marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '12px' }}>Select Classes ({selectedClasses.length}/{availableClasses.length}):</strong>
                        <button 
                          type="button" 
                          onClick={selectAllClasses}
                          style={{ 
                            background: selectedClasses.length === availableClasses.length ? '#dc2626' : '#10b981', 
                            color: 'white', 
                            border: 'none', 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            cursor: 'pointer', 
                            fontSize: '10px' 
                          }}
                        >
                          {selectedClasses.length === availableClasses.length ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '5px', maxHeight: '100px', overflowY: 'auto' }}>
                        {availableClasses.map(className => (
                          <label key={className} style={{ display: 'flex', alignItems: 'center', fontSize: '11px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedClasses.includes(className)}
                              onChange={() => handleClassSelection(className)}
                              style={{ marginRight: '4px' }}
                            />
                            {className}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <input
                    type="text"
                    placeholder="Event title"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '10px' }}
                  />
                  <button type="submit" style={{
                    background: editingEvent ? '#f59e0b' : (userRole === 'admin' || userRole === 'admin1') ? '#dc2626' : '#10b981',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px'
                  }}>
                    {editingEvent ? '✏️ Update Event' : (userRole === 'admin' || userRole === 'admin1') ? '🔧 Add for Selected Classes' : 'Add Event'}
                  </button>
                  {editingEvent && (
                    <button type="button" onClick={cancelEdit} style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}>
                      Cancel
                    </button>
                  )}
                </form>
                
                <div style={{ marginTop: '15px', maxHeight: '150px', overflow: 'auto' }}>
                  <h5>Existing Events:</h5>
                  {events.map(event => (
                    <div key={event._id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '5px',
                      background: editingEvent?._id === event._id ? '#fef3c7' : '#f8f9fa',
                      borderRadius: '4px',
                      marginBottom: '5px',
                      border: editingEvent?._id === event._id ? '1px solid #f59e0b' : 'none'
                    }}>
                      <span style={{ fontSize: '12px', flex: 1 }}>
                        {event.date} - {event.title} ({event.type})
                        {event.isAdminEvent && <span style={{ color: '#dc2626', fontWeight: 'bold' }}> • Admin</span>}
                      </span>
                      {(userRole === 'admin' || userRole === 'admin1') && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => editEvent(event)} style={{
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px'
                          }}>Edit</button>
                          <button onClick={() => deleteEvent(event._id)} style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '10px'
                          }}>Delete</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Calendar;